<script>
import * as presetDriverBehavior from '@/services/rest-api-sms/lib/preset-driver-behavior/preset-driver-behavior'
import Confirmation from '@/components/widgets/modals/Confirmation'

export default {
  components: { Confirmation },
  props: {
    sensor: { type: Array, default: () => [] },
    vehicle: { type: Object, default: null },
    modal: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    region: { type: String, default: 'SG' },
  },
  data() {
    return {
      modalConfirm: false,
      originalFormData: null,
      errors: {
        driverBehaviourMode: null,
        vehicleType: null,
      },
      form: {
        driverBehaviourMode: null,
        vehicleType: null,
        orientation: 0,
        harshAcceleration: null,
        harshBraking: null,
        harshCornering: null,
      },
      modeOptions: [
        { id: 'gnss', name: 'GNSS' },
        { id: 'accelerometer', name: 'Accelerometer' },
      ],
      vehicleTypeOptions: [],
      orientationOptions: [
        { id: 0, name: 'Arah kabel ke belakang' },
        { id: 1, name: 'Arah kabel ke kanan' },
        { id: 2, name: 'Arah kabel ke kiri' },
      ],
    }
  },
  computed: {
    showModal: {
      get() {
        return this.modal
      },
      set(v) {
        this.$emit('update:modal', v)
      },
    },
    loading: {
      get() {
        return this.isLoading
      },
      set(v) {
        this.$emit('update:isLoading', v)
      },
    },
    hasChanges() {
      if (!this.originalFormData) return false
      return JSON.stringify(this.form) !== JSON.stringify(this.originalFormData)
    },
  },
  methods: {
    async onModalShow() {
      await this.getVehicleTypes()
      this.resetForm()
      this.saveOriginalFormData()
    },
    saveOriginalFormData() {
      this.originalFormData = JSON.parse(JSON.stringify(this.form))
    },
    closeModal() {
      this.$emit('update:modal', false)
    },
    selectVehicleType(vehicleTypeId) {
      const selectedVehicleType = this.vehicleTypeOptions.find(
        (v) => v.preset_driver_behaviour_id === vehicleTypeId
      )
      this.form.harshAcceleration = selectedVehicleType.harsh_acceleration
      this.form.harshBraking = selectedVehicleType.harsh_braking
      this.form.harshCornering = selectedVehicleType.harsh_cornering
      this.clearErrors()
    },
    async getVehicleTypes(_params) {
      let params = {
        page: 1,
        limit: 99999999,
        search: this.search,
      }

      if (_params) {
        params.page = _params.page
        params.limit = _params.limit
        params.search = _params.search
      }
      const vehicleTypes = await presetDriverBehavior.get(params, this.region)

      if (!vehicleTypes?.data) return
      this.vehicleTypeOptions = vehicleTypes?.data?.paginated_result
    },
    clearErrors() {
      this.errors = {
        driverBehaviourMode: null,
        vehicleType: null,
      }
    },
    validateForm() {
      this.clearErrors()
      let isValid = true

      if (this.form.driverBehaviourMode === 'accelerometer' && !this.form.vehicleType) {
        this.errors.vehicleType = 'Jenis Kendaraan harus dipilih'
        isValid = false
      }

      return isValid
    },
    handleSubmit() {
      if (!this.validateForm()) {
        return
      }
      this.modalConfirm = true
    },
    afterConfirm() {
      const payload = {
        driver_behaviour_mode: this.form.driverBehaviourMode,
        additional_setting: {
          preset_driver_behaviour_id: this.form.vehicleType,
          device_orientation: Number(this.form.orientation),
          harsh_acceleration: Number(this.form.harshAcceleration),
          harsh_braking: Number(this.form.harshBraking),
          harsh_cornering: Number(this.form.harshCornering),
        },
        endpoint:
          this.form.driverBehaviourMode === 'accelerometer'
            ? 'driving_behavior_accelerometer'
            : 'driving_behavior_gnss',
      }

      this.$emit('submit', payload)
      this.closeModal()
    },
    resetForm() {
      this.clearErrors()
      const sBraking = this.sensor.find((s) => s.sensor_code === 16)
      const sAcceleration = this.sensor.find((s) => s.sensor_code === 17)
      const sCornering = this.sensor.find((s) => s.sensor_code === 18)

      const driverBehaviourMode = sBraking && sAcceleration && sCornering ? 'accelerometer' : 'gnss'

      this.form = {
        driverBehaviourMode,
        vehicleType: this.vehicle.preset_driver_behaviour_id || null,
        orientation: this.vehicle.device_orientation ?? 0,
        harshAcceleration: sAcceleration ? sAcceleration.alert_hi : null,
        harshBraking: sBraking ? sBraking.alert_hi : null,
        harshCornering: sCornering ? sCornering.alert_hi : null,
      }
    },
  },
}
</script>

<template>
  <div>
    <b-modal
      v-model="showModal"
      class="mc-modal"
      id="modal-driver-behavior"
      title="Pengaturan Perilaku Pengemudi"
      size="lg"
      centered
      :hide-header-close="true"
      no-close-on-backdrop
      hide-footer
      @show="onModalShow"
    >
      <div class="card-body mc-modal__filter p-2">
        <FormulateForm v-model="form" @submit="handleSubmit">
          <b-row class="mb-3">
            <b-col cols="12">
              <label class="required">Mode Perilaku Pengemudi</label>
              <v-select
                v-model="form.driverBehaviourMode"
                :options="modeOptions"
                label="name"
                :reduce="(v) => v.id"
                placeholder="Pilih Mode"
                :clearable="false"
              />
            </b-col>
          </b-row>

          <template v-if="form.driverBehaviourMode === 'accelerometer'">
            <b-row class="mb-3">
              <b-col cols="12">
                <label class="required">Jenis Kendaraan</label>
                <v-select
                  v-model="form.vehicleType"
                  :options="vehicleTypeOptions"
                  label="name"
                  :reduce="(v) => v.preset_driver_behaviour_id"
                  placeholder="Pilih Jenis Kendaraan"
                  :clearable="false"
                  :class="{ 'is-invalid': errors.vehicleType }"
                  @input="
                    (value) => {
                      selectVehicleType(value)
                    }
                  "
                />
                <div v-if="errors.vehicleType" class="invalid-feedback d-block">
                  {{ errors.vehicleType }}
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="12">
                <label>Posisi pemasangan</label>
                <v-select
                  v-model="form.orientation"
                  :options="orientationOptions"
                  label="name"
                  :reduce="(v) => v.id"
                  placeholder="Pilih Posisi Pemasangan"
                  :clearable="false"
                />
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="12">
                <FormulateInput
                  name="harshAcceleration"
                  type="number"
                  label="Harsh Acceleration Threshold (mg)"
                  placeholder="Masukkan nilai"
                  validation="required|number|between:0,1000,true"
                  validation-behavior="live"
                  error-behavior="live"
                />
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="12">
                <FormulateInput
                  name="harshBraking"
                  type="number"
                  label="Harsh Braking Threshold (mg)"
                  placeholder="Masukkan nilai"
                  validation="required|number|between:0,1000,true"
                  validation-behavior="live"
                  error-behavior="live"
                />
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="12">
                <FormulateInput
                  name="harshCornering"
                  type="number"
                  label="Harsh Cornering Threshold (mg)"
                  placeholder="Masukkan nilai"
                  validation="required|number|between:0,1000,true"
                  validation-behavior="live"
                  error-behavior="live"
                />
              </b-col>
            </b-row>
          </template>

          <b-row class="mt-4">
            <b-col cols="12" class="d-flex justify-content-end">
              <b-button variant="outline-primary" @click="closeModal" class="me-2">
                Batal
              </b-button>
              <b-button variant="primary" type="submit" :disabled="loading || !hasChanges">
                Simpan
              </b-button>
            </b-col>
          </b-row>
        </FormulateForm>
      </div>
    </b-modal>

    <confirmation
      v-if="modalConfirm"
      :modal.sync="modalConfirm"
      message="Yakin ingin menyimpan pengaturan ADAS?"
      @confirmed="afterConfirm"
      centered
    />
  </div>
</template>
